import { isNum } from "./formatUtils";

/**
 * Performance statistics over tracked trades.
 *
 * The watchlist is the trade journal — there is no separate journal — so these
 * numbers are derived from the same investment records the watchlist already
 * stores. Open and closed trades are treated differently on purpose:
 *
 *   - Unrealized P/L uses the live price and only exists while a trade is open.
 *   - Realized P/L uses the recorded exit price and only exists once closed.
 *   - Win rate and the averages are computed from CLOSED trades only. Including
 *     open positions would let a user's win rate drift with intraday noise and
 *     would flatter results by counting unfinished trades that may still turn.
 *
 * Every statistic returns null (never 0) when there isn't enough data to
 * compute it honestly.
 */

/** Percentage return of a single trade, or null if it can't be computed. */
export function tradeReturnPct(entryPrice, exitOrCurrentPrice) {
  if (!isNum(entryPrice) || !isNum(exitOrCurrentPrice) || entryPrice <= 0) return null;
  return ((exitOrCurrentPrice - entryPrice) / entryPrice) * 100;
}

/**
 * Cash profit or loss on a position, from the amount that went into it.
 *
 * A percentage answers "how did the stock do"; this answers "how much of my
 * money is that". Both are kept — the percentage is what compares trades to
 * each other, the cash figure is what the position actually did.
 *
 * Returns null, never 0, when the amount invested was never recorded. A trade
 * tracked before this existed has an unknown P/L, and showing "$0.00" would
 * claim it broke even.
 */
export function tradePnlAmount(amountInvested, returnPct) {
  if (!isNum(amountInvested) || amountInvested <= 0 || !isNum(returnPct)) return null;
  return amountInvested * (returnPct / 100);
}

/** Shares the amount bought at the entry price, or null if either is unknown. */
export function tradeShares(amountInvested, entryPrice) {
  if (!isNum(amountInvested) || amountInvested <= 0) return null;
  if (!isNum(entryPrice) || entryPrice <= 0) return null;
  return amountInvested / entryPrice;
}

/**
 * Normalizes one stored investment record plus its live quote into a row the
 * UI can render directly.
 */
export function buildTradeRow(symbol, inv, liveStock) {
  const isClosed     = inv?.status === "closed" && isNum(inv?.exitPrice);
  const currentPrice = isNum(liveStock?.price) ? liveStock.price : null;
  const exitPrice    = isNum(inv?.exitPrice) ? inv.exitPrice : null;

  const unrealizedPct = isClosed ? null : tradeReturnPct(inv?.entryPrice, currentPrice);
  const realizedPct   = isClosed ? tradeReturnPct(inv?.entryPrice, exitPrice) : null;

  // How much money actually went in. Optional — positions tracked before this
  // was recorded keep working, they simply have no cash figures.
  const amountInvested = isNum(inv?.amountInvested) && inv.amountInvested > 0 ? inv.amountInvested : null;
  const shares         = tradeShares(amountInvested, inv?.entryPrice);
  const unrealizedPnl  = tradePnlAmount(amountInvested, unrealizedPct);
  const realizedPnl    = tradePnlAmount(amountInvested, realizedPct);
  const pnlAmount      = isClosed ? realizedPnl : unrealizedPnl;
  // What the position is worth now (open) or was worth on exit (closed).
  const currentValue   = amountInvested !== null && isNum(pnlAmount) ? amountInvested + pnlAmount : null;

  return {
    symbol,
    entryDate:     isNum(inv?.addedAt) ? inv.addedAt : null,
    entryPrice:    isNum(inv?.entryPrice) ? inv.entryPrice : null,
    currentPrice,
    amountInvested,
    shares,
    unrealizedPnl,
    realizedPnl,
    pnlAmount,
    currentValue,
    entryScore:    isNum(inv?.entryScore) ? inv.entryScore : null,
    currentScore:  isNum(liveStock?.score) ? liveStock.score : null,
    stopPrice:     isNum(inv?.stopPrice) ? inv.stopPrice : null,
    targetPrice:   isNum(inv?.targetPrice) ? inv.targetPrice : null,
    unrealizedPct,
    realizedPct,
    status:        isClosed ? "closed" : "open",
    exitPrice,
    closedAt:      isNum(inv?.closedAt) ? inv.closedAt : null,
  };
}

/** Aggregate statistics across all trade rows. Nulls where not computable. */
export function computeTradeStats(rows) {
  const closed = rows.filter((r) => r.status === "closed" && isNum(r.realizedPct));
  const open   = rows.filter((r) => r.status === "open");

  const openUnrealized = open.map((r) => r.unrealizedPct).filter(isNum);
  const returns        = closed.map((r) => r.realizedPct);
  const winners        = returns.filter((r) => r > 0);
  const losers         = returns.filter((r) => r <= 0);

  const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

  let best = null, worst = null;
  closed.forEach((r) => {
    if (!best  || r.realizedPct > best.realizedPct)  best  = r;
    if (!worst || r.realizedPct < worst.realizedPct) worst = r;
  });

  /**
   * Cash totals.
   *
   * Summed only over the positions that actually recorded an amount, and the
   * count of those is returned alongside so the UI can say what the total
   * covers. A portfolio where half the trades predate this feature would
   * otherwise show a total that silently omits them.
   */
  const withAmount        = rows.filter((r) => isNum(r.amountInvested));
  const openWithAmount    = withAmount.filter((r) => r.status === "open");
  const closedWithAmount  = withAmount.filter((r) => r.status === "closed");
  const sum = (arr, key) => {
    const vals = arr.map((r) => r[key]).filter(isNum);
    return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
  };

  const openInvested     = sum(openWithAmount, "amountInvested");
  const unrealizedPnl    = sum(openWithAmount, "unrealizedPnl");
  const realizedPnl      = sum(closedWithAmount, "realizedPnl");

  return {
    openCount:        open.length,
    closedCount:      closed.length,
    // Cash view — null (not 0) wherever no position recorded an amount.
    trackedWithAmount: withAmount.length,
    openInvested,
    openValue:        openInvested !== null && isNum(unrealizedPnl) ? openInvested + unrealizedPnl : openInvested,
    unrealizedPnl,
    realizedPnl,
    totalPnl:
      isNum(unrealizedPnl) || isNum(realizedPnl)
        ? (isNum(unrealizedPnl) ? unrealizedPnl : 0) + (isNum(realizedPnl) ? realizedPnl : 0)
        : null,
    // Win rate is meaningless with no completed trades — null, not 0%.
    winRate:          closed.length ? (winners.length / closed.length) * 100 : null,
    avgReturn:        mean(returns),
    avgWinner:        mean(winners),
    avgLoser:         mean(losers),
    bestTrade:        best,
    worstTrade:       worst,
    totalRealizedPct: returns.length ? returns.reduce((a, b) => a + b, 0) : null,
    avgUnrealizedPct: mean(openUnrealized),
  };
}
